/**
 * SIMS-8062 / NFR-4 — the Program Activity Summary PDF as a STRUCTURED document.
 *
 * The export used to rasterise the report with html2canvas: one tall PNG sliced across
 * pages. It looked right and it satisfied FR-17, but a picture of a report is not a
 * report — no selectable text, nothing searchable, nothing a screen reader can read, and
 * a file several megabytes wide. That is NFR-4, and it is what this module answers.
 *
 * The document is typeset from the same values the screen renders, in the same order,
 * with the same headings and the same copy. It is not a pixel-for-pixel photograph of
 * the page and cannot be: real glyphs reflow. What it preserves is everything a reader
 * of the report cares about — which sections, in which order, with which numbers.
 *
 * Split in two on purpose:
 *
 *  - `buildProgramSummaryPdfModel` is pure. It decides what the document SAYS, and every
 *    rule that could be got wrong (ordering, totals, an absent bucket, a program with no
 *    results) is pinned by unit tests without a PDF library in sight.
 *  - `renderProgramSummaryPdf` decides what it LOOKS LIKE. It holds no report rules.
 *
 * Nothing here reads the DOM, so the export no longer depends on the report being
 * on-screen, laid out, or scrolled into view.
 */

import type jsPDF from "jspdf";
import type { UserOptions } from "jspdf-autotable";

import { visitBucketColumns, visitRowFor } from "./programSelection";

/** A single labelled figure, already formatted for display. */
export interface PdfMetric {
	label: string;
	value: string;
}

/** One program's block inside a metric panel. */
export interface PdfProgramMetrics {
	name: string;
	/**
	 * SIMS-8904 — the filters applied to THIS program, printed under its heading.
	 *
	 * The PDF used to carry a single context line at the top of the document. Once
	 * SIMS-8038 split the report into a section per program, that one line could
	 * not describe them: each program has its own org-unit narrowing, so a shared
	 * line is either wrong for the others or has to drop the per-program parts
	 * entirely. The screen states it per section; the export now does the same.
	 */
	contextLine?: string;
	metrics: PdfMetric[];
}

/** Enrollment / Incentive: labelled figures, per program, then a total. */
export interface PdfMetricPanel {
	title: string;
	note: string;
	/** Per-program blocks. Empty when the API predates SIMS-8038. */
	programs: PdfProgramMetrics[];
	/** The combined figures — the "All selected programs" band, or the only block. */
	total: PdfMetric[];
	/** Rendered only when there are per-program blocks to total. */
	showTotal: boolean;
	footnote?: string;
}

/** One program's Visit Results table. */
export interface PdfVisitProgram {
	name: string;
	/** SIMS-8904 — this program's applied filters, as in PdfProgramMetrics. */
	contextLine?: string;
	columns: string[];
	rows: Array<{ event: string; values: Array<number | null> }>;
	/** Copy shown instead of a table when the program returned nothing. */
	emptyText?: string;
}

export interface PdfVisitPanel {
	title: string;
	note: string;
	programs: PdfVisitProgram[];
}

export interface ProgramSummaryPdfModel {
	title: string;
	/** The applied-filter context line, or "" when nothing is narrowed. */
	filters: string;
	/** The exclusion banner's text, when programs were hidden. */
	notice?: string;
	metricPanels: PdfMetricPanel[];
	visitPanel: PdfVisitPanel;
}

/** The shape the builder needs from one program's breakdown entry. */
export interface PdfBreakdownEntry {
	programId: string;
	programName: string;
	/**
	 * SIMS-8904 — the program's own "Applied:" line, resolved by the caller with
	 * `programContextLine()`, the same helper the screen's ProgramHeading uses. It
	 * is resolved there rather than here because only the tab can turn filter ids
	 * into display names, and reusing the screen's helper is what keeps the two
	 * from drifting.
	 */
	contextLine?: string;
	enrollmentCounts?: unknown;
	incentiveSummary?: unknown;
	visitResultsByEvent: ReadonlyArray<{
		eventId?: string;
		eventName?: string | null;
		buckets: ReadonlyArray<{ label: string; count: number }>;
	}>;
}

export interface PdfModelInput {
	title: string;
	filters: string;
	notice?: string;
	/** Already sorted alphabetically by the caller — the screen's order (AC2). */
	breakdown: ReadonlyArray<PdfBreakdownEntry>;
	/** Formats one program's enrollment figures. Reuses the screen's own labels. */
	enrollmentCellsFor: (src: unknown) => PdfMetric[];
	incentiveCellsFor: (src: unknown) => PdfMetric[];
	/** The union figures, for the "All selected programs" band. */
	enrollmentTotal: PdfMetric[];
	incentiveTotal: PdfMetric[];
	enrollmentNote: string;
	incentiveNote: string;
	visitNote: string;
	enrollmentFootnote?: string;
	visitEmptyText: string;
	/** The combined Visit Results, used when there is no per-program breakdown. */
	combinedVisitResults: PdfBreakdownEntry["visitResultsByEvent"];
}

/**
 * Assemble the document's content.
 *
 * The one rule with teeth: the total band is shown only when there are per-program
 * blocks to total. Without a breakdown the panel already IS the total, and repeating it
 * under an "All selected programs" heading would read as a second, different figure —
 * the same trap SIMS-8041 avoids on screen.
 */
export function buildProgramSummaryPdfModel(
	input: PdfModelInput,
): ProgramSummaryPdfModel {
	const perProgram = input.breakdown.length > 0;

	const metricPanels: PdfMetricPanel[] = [
		{
			title: "ENROLLMENT SUMMARY",
			note: input.enrollmentNote,
			programs: input.breakdown.map((p) => ({
				name: p.programName,
				contextLine: p.contextLine,
				metrics: input.enrollmentCellsFor(p.enrollmentCounts),
			})),
			total: input.enrollmentTotal,
			showTotal: perProgram,
			footnote: perProgram ? input.enrollmentFootnote : undefined,
		},
		{
			title: "INCENTIVE SUMMARY",
			note: input.incentiveNote,
			programs: input.breakdown.map((p) => ({
				name: p.programName,
				contextLine: p.contextLine,
				metrics: input.incentiveCellsFor(p.incentiveSummary),
			})),
			total: input.incentiveTotal,
			showTotal: perProgram,
		},
	];

	const visitProgramFrom = (
		name: string,
		events: PdfBreakdownEntry["visitResultsByEvent"],
		contextLine?: string,
	): PdfVisitProgram => {
		const columns = visitBucketColumns(events);
		// A program with no results still gets its block: omitting it is
		// indistinguishable from the program having been EXCLUDED (SIMS-8056 AC2).
		if (columns.length === 0) {
			return {
				name,
				contextLine,
				columns: [],
				rows: [],
				emptyText: input.visitEmptyText,
			};
		}
		return {
			name,
			contextLine,
			columns,
			rows: events.map((ev) => ({
				event: ev.eventName ?? "Event",
				values: visitRowFor(ev, columns),
			})),
		};
	};

	const visitPanel: PdfVisitPanel = {
		title: "VISIT RESULTS",
		note: input.visitNote,
		programs: perProgram
			? input.breakdown.map((p) =>
					visitProgramFrom(p.programName, p.visitResultsByEvent, p.contextLine),
				)
			: [visitProgramFrom("", input.combinedVisitResults)],
	};

	return {
		title: input.title,
		filters: input.filters,
		notice: input.notice,
		metricPanels,
		visitPanel,
	};
}

/** Colours the document uses, passed in so the caller stays the single source of theme. */
export interface PdfPalette {
	ink: string;
	muted: string;
	band: string;
	bandInk: string;
	total: string;
	border: string;
}

/** The autoTable entry point, injected so tests do not need a real PDF engine. */
export type AutoTableFn = (doc: jsPDF, options: UserOptions) => void;

const MARGIN = 40;
const EM_DASH = "—";

/**
 * A CSS colour to the [r,g,b] triple jsPDF wants.
 *
 * Handles `#rgb`, `#rrggbb` and `rgb()/rgba()`, because these values come from the MUI
 * palette — which is fed by each account's configurable themeColors and hands back
 * whichever form was configured. Anything unrecognised falls back to black rather than
 * throwing: a wrong colour is a blemish, a crash loses the export.
 */
export function rgb(color: string): [number, number, number] {
	const value = color.trim();

	const short = /^#([0-9a-f]{3})$/i.exec(value);
	if (short) {
		const [r, g, b] = short[1].split("");
		return [parseInt(r + r, 16), parseInt(g + g, 16), parseInt(b + b, 16)];
	}

	const long = /^#([0-9a-f]{6})$/i.exec(value);
	if (long) {
		const n = parseInt(long[1], 16);
		return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
	}

	const fn = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(value);
	if (fn) {
		const clamp = (s: string): number =>
			Math.max(0, Math.min(255, Math.round(Number(s))));
		return [clamp(fn[1]), clamp(fn[2]), clamp(fn[3])];
	}

	return [0, 0, 0];
}

/**
 * Render the model into `doc`.
 *
 * Page breaks are autoTable's for tables. For the free text between them this tracks the
 * cursor and starts a new page when a block would not fit, so a program heading is never
 * left stranded at the foot of a page above its own table.
 */
export function renderProgramSummaryPdf(
	doc: jsPDF,
	model: ProgramSummaryPdfModel,
	palette: PdfPalette,
	autoTable: AutoTableFn,
): void {
	const pageW = doc.internal.pageSize.getWidth();
	const pageH = doc.internal.pageSize.getHeight();
	const contentW = pageW - MARGIN * 2;
	let y = MARGIN;

	const space = (needed: number): void => {
		if (y + needed <= pageH - MARGIN) return;
		doc.addPage();
		y = MARGIN;
	};

	/** Wrapped paragraph in the muted tone; returns nothing, advances the cursor. */
	const paragraph = (text: string, size: number, color: string): void => {
		if (!text) return;
		doc.setFontSize(size);
		doc.setTextColor(...rgb(color));
		const lines = doc.splitTextToSize(text, contentW) as string[];
		space(lines.length * (size + 2));
		doc.text(lines, MARGIN, y);
		y += lines.length * (size + 2) + 4;
	};

	const afterTable = (): void => {
		const last = (doc as unknown as { lastAutoTable?: { finalY: number } })
			.lastAutoTable;
		y = (last?.finalY ?? y) + 14;
	};

	doc.setFont("helvetica", "bold");
	doc.setFontSize(16);
	doc.setTextColor(...rgb(palette.ink));
	doc.text(model.title, MARGIN, y);
	y += 22;

	doc.setFont("helvetica", "normal");
	paragraph(model.filters, 9, palette.muted);
	paragraph(model.notice ?? "", 9, palette.muted);
	y += 4;

	/** A panel's title band — the screen's grey header strip. */
	const band = (title: string, note: string): void => {
		space(46);
		doc.setFillColor(...rgb(palette.band));
		doc.rect(MARGIN, y, contentW, 20, "F");
		doc.setFont("helvetica", "bold");
		doc.setFontSize(10);
		doc.setTextColor(...rgb(palette.bandInk));
		doc.text(title, MARGIN + 8, y + 14);
		y += 26;
		doc.setFont("helvetica", "normal");
		paragraph(note, 8, palette.muted);
	};

	const programHeading = (name: string): void => {
		if (!name) return;
		space(34);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(11);
		doc.setTextColor(...rgb(palette.ink));
		doc.text(name, MARGIN, y + 10);
		y += 20;
		doc.setFont("helvetica", "normal");
	};

	/** One block of labelled figures: labels as the head row, values beneath. */
	const metricTable = (metrics: PdfMetric[], emphasise: boolean): void => {
		if (metrics.length === 0) return;
		space(60);
		autoTable(doc, {
			startY: y,
			margin: { left: MARGIN, right: MARGIN },
			head: [metrics.map((m) => m.label)],
			body: [metrics.map((m) => m.value)],
			theme: "grid",
			styles: { fontSize: 9, cellPadding: 6, lineColor: rgb(palette.border) },
			headStyles: {
				fontSize: 7,
				fontStyle: "bold",
				fillColor: false,
				textColor: rgb(palette.muted),
			},
			bodyStyles: {
				fontSize: emphasise ? 13 : 12,
				fontStyle: "bold",
				textColor: rgb(emphasise ? palette.total : palette.ink),
			},
		});
		afterTable();
	};

	for (const panel of model.metricPanels) {
		band(panel.title, panel.note);
		for (const p of panel.programs) {
			programHeading(p.name);
			// SIMS-8904 AC1: this program's applied filters, under its own heading —
			// the export's counterpart to the screen's per-section "Applied:" line.
			// Skipped when empty rather than printing a placeholder: a filter nobody
			// set did not narrow the query.
			paragraph(p.contextLine ?? "", 8, palette.muted);
			metricTable(p.metrics, false);
		}
		if (panel.showTotal) {
			programHeading("All selected programs");
			metricTable(panel.total, true);
		} else {
			metricTable(panel.total, false);
		}
		paragraph(panel.footnote ?? "", 8, palette.muted);
		y += 6;
	}

	band(model.visitPanel.title, model.visitPanel.note);
	for (const p of model.visitPanel.programs) {
		programHeading(p.name);
		// SIMS-8904 AC1 — same per-program line in Visit Results.
		paragraph(p.contextLine ?? "", 8, palette.muted);
		if (p.columns.length === 0) {
			paragraph(p.emptyText ?? "", 9, palette.muted);
			continue;
		}
		space(60);
		autoTable(doc, {
			startY: y,
			margin: { left: MARGIN, right: MARGIN },
			head: [["Event", ...p.columns]],
			// An absent bucket is an em dash, never 0 — a zero would assert a
			// measurement that returned nothing (SIMS-8056 AC1).
			body: p.rows.map((r) => [
				r.event,
				...r.values.map((v) => (v === null ? EM_DASH : String(v))),
			]),
			theme: "grid",
			styles: { fontSize: 9, cellPadding: 6, lineColor: rgb(palette.border) },
			headStyles: {
				fontSize: 7,
				fontStyle: "bold",
				fillColor: false,
				textColor: rgb(palette.muted),
			},
			columnStyles: { 0: { halign: "left" } },
			didParseCell: (data: {
				column: { index: number };
				cell: { styles: { halign: string } };
			}) => {
				if (data.column.index > 0) data.cell.styles.halign = "right";
			},
		} as UserOptions);
		afterTable();
	}
}
